import { useCallback, useEffect, useMemo, useReducer } from 'react'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase.js'

// State reducer for efficient state management and minimal re-renders
const deviceStatusReducer = (state, action) => {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true, error: null }
    case 'SUCCESS':
      return { status: action.payload, loading: false, error: null }
    case 'ERROR':
      return { status: null, loading: false, error: action.payload }
    case 'RESET':
      return { status: null, loading: false, error: null }
    default:
      return state
  }
}

/**
 * Firebase v11+ Device Status Hook
 * 
 * React 19+ optimized real-time device status monitoring with minimal re-renders.
 * Efficiently tracks device connectivity, health, and operational status from Firestore.
 * 
 * Features:
 * - useReducer for optimal state management
 * - Memoized snapshot handlers for performance
 * - Automatic Firestore timestamp conversion
 * - Real-time updates with efficient cleanup
 * - Error boundaries integration ready
 * 
 * @param {string|null} deviceId - Device serial number/ID to monitor
 * @returns {Object} Device status state with loading/error states
 */
export function useDeviceStatus(deviceId) {
  const [state, dispatch] = useReducer(deviceStatusReducer, {
    status: null,
    loading: false,
    error: null,
  })

  // Memoized snapshot handler to prevent re-renders
  const handleSnapshot = useCallback((doc) => {
    if (doc.exists()) {
      const data = doc.data()
      
      // Keep Firestore timestamps as-is for compatibility with existing components
      // Components like DeviceInfo.jsx expect Firestore Timestamp objects with .toMillis() method
      const deviceStatus = {
        id: doc.id,
        ...data,
        // Preserve original Firestore timestamps for component compatibility
        lastSeen: data.lastSeen ?? null,
        lastUpdated: data.lastUpdated ?? null,
        connectedAt: data.connectedAt ?? null,
      }
      
      dispatch({ type: 'SUCCESS', payload: deviceStatus })
    } else {
      dispatch({ type: 'SUCCESS', payload: null })
    }
  }, [])

  // Memoized error handler for consistent error management
  const handleError = useCallback((err) => {
    console.error('Device status listener error:', err)
    dispatch({ 
      type: 'ERROR', 
      payload: err.message || 'Failed to load device status',
    })
  }, [])

  // Real-time subscription effect with proper cleanup
  useEffect(() => {
    if (!deviceId?.trim()) {
      dispatch({ type: 'RESET' })
      return
    }

    dispatch({ type: 'LOADING' })
    
    const deviceRef = doc(db, 'deviceStatus', deviceId)
    const unsubscribe = onSnapshot(deviceRef, handleSnapshot, handleError)
    
    return unsubscribe
    // handleSnapshot and handleError are stable (useCallback with no deps)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId])

  // Return memoized state to prevent unnecessary re-renders
  return useMemo(() => state, [state])
}
